/**
 * Authentication Performance Optimization System
 * 
 * This system addresses multiple authentication bottlenecks:
 * 1. Multiple redundant API calls on app startup
 * 2. Blocking authentication checks
 * 3. Heavy component initialization during auth flow
 * 4. Lack of authentication caching
 * 5. No progressive loading strategy
 */

class AuthPerformanceOptimizer {
    constructor() {
        this.optimizationConfig = {
            // Cache tokens for faster validation
            tokenCacheEnabled: true,
            tokenCacheExpiry: 5 * 60 * 1000, // 5 minutes
            
            // Defer heavy operations until after initial render
            deferredLoadDelay: 100, // ms
            
            // Progressive loading strategy
            criticalApiCalls: ['auth/verify', 'user/profile'],
            deferredApiCalls: ['rag/status', 'nx/websocket', 'settings/system'],
            
            // Performance monitoring
            enablePerformanceLogging: true,
            authTimeoutWarning: 3000, // 3 seconds
        };
        
        this.performanceLog = [];
        this.startTime = null;
    }

    // Performance monitoring
    startTiming(operation) {
        if (this.optimizationConfig.enablePerformanceLogging) {
            this.startTime = performance.now();
            console.log(`🚀 [Auth Perf] Starting: ${operation}`);
        }
    }

    endTiming(operation, result = 'success') {
        if (this.optimizationConfig.enablePerformanceLogging && this.startTime) {
            const duration = performance.now() - this.startTime;
            const logEntry = {
                operation,
                duration: Math.round(duration),
                result,
                timestamp: new Date().toISOString()
            };
            
            this.performanceLog.push(logEntry);
            
            const emoji = duration > this.optimizationConfig.authTimeoutWarning ? '🐌' : '⚡';
            console.log(`${emoji} [Auth Perf] ${operation}: ${Math.round(duration)}ms (${result})`);
            
            if (duration > this.optimizationConfig.authTimeoutWarning) {
                console.warn(`⚠️ [Auth Perf] Slow authentication operation detected: ${operation} took ${Math.round(duration)}ms`);
            }
        }
    }

    // Get performance report
    getPerformanceReport() {
        return {
            totalOperations: this.performanceLog.length,
            averageTime: this.performanceLog.reduce((sum, log) => sum + log.duration, 0) / this.performanceLog.length,
            slowOperations: this.performanceLog.filter(log => log.duration > this.optimizationConfig.authTimeoutWarning),
            recentOperations: this.performanceLog.slice(-10),
            recommendations: this.generateRecommendations()
        };
    }

    generateRecommendations() {
        const recommendations = [];
        const slowOps = this.performanceLog.filter(log => log.duration > this.optimizationConfig.authTimeoutWarning);
        
        if (slowOps.length > 0) {
            recommendations.push({
                type: 'CRITICAL',
                issue: `${slowOps.length} slow authentication operations detected`,
                suggestion: 'Consider implementing token caching and API call deferring',
                impact: 'HIGH'
            });
        }
        
        const authOps = this.performanceLog.filter(log => log.operation.includes('auth'));
        if (authOps.length > 3) {
            recommendations.push({
                type: 'WARNING',
                issue: 'Multiple authentication checks detected',
                suggestion: 'Implement authentication result caching',
                impact: 'MEDIUM'
            });
        }
        
        return recommendations;
    }

    // Clear performance log
    clearPerformanceLog() {
        this.performanceLog = [];
        console.log('📊 [Auth Perf] Performance log cleared');
    }
}

// Create global instance
export const authPerformanceOptimizer = new AuthPerformanceOptimizer();

// Export optimization utilities
export const AuthOptimizations = {
    
    // Fast token validation with caching
    fastTokenValidation: (() => {
        let tokenCache = null;
        let cacheExpiry = 0;
        
        return (token) => {
            authPerformanceOptimizer.startTiming('fastTokenValidation');
            
            try {
                // Check cache first
                if (tokenCache && Date.now() < cacheExpiry && tokenCache.token === token) {
                    authPerformanceOptimizer.endTiming('fastTokenValidation', 'cached');
                    return tokenCache.decoded;
                }
                
                // Decode token
                const decoded = JSON.parse(atob(token.split('.')[1]));
                
                // Cache the result
                tokenCache = { token, decoded };
                cacheExpiry = Date.now() + authPerformanceOptimizer.optimizationConfig.tokenCacheExpiry;
                
                authPerformanceOptimizer.endTiming('fastTokenValidation', 'decoded');
                return decoded;
                
            } catch (error) {
                authPerformanceOptimizer.endTiming('fastTokenValidation', 'error');
                return null;
            }
        };
    })(),
    
    // Deferred API call manager
    deferredApiManager: (() => {
        const deferredCalls = [];
        let isProcessing = false;
        
        return {
            defer: (apiCall, priority = 'low') => {
                deferredCalls.push({ apiCall, priority, added: Date.now() });
                console.log(`📋 [Auth Perf] Deferred API call (${priority} priority)`);
            },
            
            processDeferredCalls: async () => {
                if (isProcessing) return;
                isProcessing = true;
                
                authPerformanceOptimizer.startTiming('processDeferredCalls');
                
                // Sort by priority (high first)
                deferredCalls.sort((a, b) => {
                    const priorities = { high: 3, medium: 2, low: 1 };
                    return priorities[b.priority] - priorities[a.priority];
                });
                
                console.log(`📋 [Auth Perf] Processing ${deferredCalls.length} deferred API calls`);
                
                // Process calls with delays to avoid overwhelming the server
                for (let i = 0; i < deferredCalls.length; i++) {
                    const { apiCall } = deferredCalls[i];
                    try {
                        await apiCall();
                    } catch (error) {
                        console.warn('📋 [Auth Perf] Deferred API call failed:', error);
                    }
                    
                    // Small delay between calls
                    if (i < deferredCalls.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }
                }
                
                deferredCalls.length = 0; // Clear the array
                isProcessing = false;
                
                authPerformanceOptimizer.endTiming('processDeferredCalls');
            }
        };
    })(),
    
    // Progressive component loader
    progressiveLoader: {
        loadCriticalComponents: () => {
            authPerformanceOptimizer.startTiming('loadCriticalComponents');
            
            // Load only essential components for initial render
            const criticalComponents = [
                'ProtectedRoute',
                'LoadingFallback',
                'Login',
                'Navigation'
            ];
            
            console.log(`🎯 [Auth Perf] Loading critical components: ${criticalComponents.join(', ')}`);
            authPerformanceOptimizer.endTiming('loadCriticalComponents');
            
            return criticalComponents;
        },
        
        loadSecondaryComponents: () => {
            authPerformanceOptimizer.startTiming('loadSecondaryComponents');
            
            // Defer heavy components
            setTimeout(() => {
                const secondaryComponents = [
                    'Dashboard',
                    'RagChat',
                    'AppGuide',
                    'PerformanceTesting'
                ];
                
                console.log(`⏳ [Auth Perf] Loading secondary components: ${secondaryComponents.join(', ')}`);
                authPerformanceOptimizer.endTiming('loadSecondaryComponents');
            }, authPerformanceOptimizer.optimizationConfig.deferredLoadDelay);
        }
    },
    
    // Authentication state manager
    authStateManager: (() => {
        let authState = {
            isAuthenticated: null,
            user: null,
            role: null,
            checkedAt: 0
        };
        
        const CACHE_DURATION = 30 * 1000; // 30 seconds
        
        return {
            getAuthState: () => {
                authPerformanceOptimizer.startTiming('getAuthState');
                
                // Return cached state if still valid
                if (authState.checkedAt > Date.now() - CACHE_DURATION) {
                    authPerformanceOptimizer.endTiming('getAuthState', 'cached');
                    return authState;
                }
                
                // Check localStorage for token
                const token = localStorage.getItem('token');
                if (!token) {
                    authState = { isAuthenticated: false, user: null, role: null, checkedAt: Date.now() };
                    authPerformanceOptimizer.endTiming('getAuthState', 'no-token');
                    return authState;
                }
                
                // Fast token validation
                const decoded = AuthOptimizations.fastTokenValidation(token);
                if (!decoded || decoded.exp * 1000 < Date.now()) {
                    authState = { isAuthenticated: false, user: null, role: null, checkedAt: Date.now() };
                    authPerformanceOptimizer.endTiming('getAuthState', 'expired');
                    return authState;
                }
                
                authState = {
                    isAuthenticated: true,
                    user: decoded,
                    role: decoded.role,
                    checkedAt: Date.now()
                };
                
                authPerformanceOptimizer.endTiming('getAuthState', 'valid');
                return authState;
            },
            
            invalidateCache: () => {
                authState.checkedAt = 0;
                console.log('🔄 [Auth Perf] Authentication cache invalidated');
            }
        };
    })(),
    
    // Optimized route resolution
    fastRouteResolver: (userRole) => {
        authPerformanceOptimizer.startTiming('fastRouteResolver');
        
        const routes = {
            superadmin: '/superadmin/dashboard',
            admin: '/dashboard',
            member: '/dashboard',
            default: '/login'
        };
        
        const route = routes[userRole] || routes.default;
        authPerformanceOptimizer.endTiming('fastRouteResolver');
        
        return route;
    }
};

// Initialize performance monitoring
console.log('🚀 [Auth Perf] Authentication Performance Optimizer initialized');

export default AuthPerformanceOptimizer;
